import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  History, 
  Play, 
  XCircle, 
  KeyRound, 
  Gift, 
  Loader2,
  Clock,
  Trash2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface AdminAction {
  id: string;
  action_type: string;
  admin_user_id: string;
  target_user_id: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

interface AdminActionsHistoryProps {
  targetUserId: string;
}

const actionConfig: Record<string, { 
  icon: React.ComponentType<{ className?: string }>; 
  label: string; 
  color: string;
  bgColor: string;
}> = {
  membership_activate: {
    icon: Play,
    label: 'Membresía reactivada',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  membership_activation: {
    icon: Play,
    label: 'Membresía activada',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  membership_deactivate: {
    icon: XCircle,
    label: 'Membresía cancelada',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  pin_change: {
    icon: KeyRound,
    label: 'PIN actualizado',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
  benefit_redemption: {
    icon: Gift,
    label: 'Canje registrado',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
  },
  redemption_deleted: {
    icon: Trash2,
    label: 'Canje eliminado',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  purchase_deleted: {
    icon: Trash2,
    label: 'Compra eliminada',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
};

export const AdminActionsHistory: React.FC<AdminActionsHistoryProps> = ({ 
  targetUserId 
}) => {
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [adminNames, setAdminNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActions = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('admin_audit_logs')
          .select('*')
          .eq('target_user_id', targetUserId)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) {
          console.error('Error fetching admin actions:', error);
          return;
        }

        const actionsData = (data as AdminAction[]) || [];
        setActions(actionsData);

        // Fetch admin names for all unique admin_user_ids
        const uniqueAdminIds = [...new Set(actionsData.map(a => a.admin_user_id))];
        if (uniqueAdminIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id, nombre')
            .in('user_id', uniqueAdminIds);
          
          if (profiles) {
            const namesMap: Record<string, string> = {};
            profiles.forEach(p => {
              namesMap[p.user_id] = p.nombre.split(' ')[0]; // First name
            });
            setAdminNames(namesMap);
          }
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (targetUserId) {
      fetchActions();
    }
  }, [targetUserId]);

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-white/50" />
        </div>
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-white/70" />
          <h3 className="text-white font-medium">Historial de Acciones Admin</h3>
        </div>
        <p className="text-white/40 text-sm text-center py-4">
          No hay acciones registradas para este miembro
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10"
    >
      <div className="flex items-center gap-2 mb-4">
        <History className="w-5 h-5 text-white/70" />
        <h3 className="text-white font-medium">Historial de Acciones Admin</h3>
      </div>

      <div className="space-y-3 max-h-64 overflow-y-auto">
        {actions.map((action) => {
          const config = actionConfig[action.action_type] || {
            icon: Clock,
            label: action.action_type,
            color: 'text-white/70',
            bgColor: 'bg-white/10',
          };
          const IconComponent = config.icon;

          return (
            <div
              key={action.id}
              className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/5"
            >
              <div className={`p-2 rounded-lg ${config.bgColor}`}>
                <IconComponent className={`w-4 h-4 ${config.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{config.label}</p>
                {action.details && action.action_type === 'membership_activation' && (
                  <div className="text-white/50 text-xs mt-0.5 space-y-0.5">
                    <p>
                      <span className="text-amber-400 capitalize">{(action.details as { plan?: string }).plan}</span>
                      {' • '}
                      <span className="text-green-400">₡{((action.details as { amount?: number }).amount || 0).toLocaleString()}</span>
                    </p>
                    {(action.details as { sold_by_name?: string }).sold_by_name && (
                      <p className="text-cyan-400">
                        Vendedor: {(action.details as { sold_by_name?: string }).sold_by_name}
                      </p>
                    )}
                    {(action.details as { notes?: string }).notes && (
                      <p className="text-white/40 italic">"{(action.details as { notes?: string }).notes}"</p>
                    )}
                  </div>
                )}
                {action.details && action.action_type === 'benefit_redemption' && (
                  <p className="text-white/50 text-xs mt-0.5">
                    {(action.details as { quantity?: number }).quantity || 1}x {' '}
                    {(action.details as { benefit_type?: string }).benefit_type}
                  </p>
                )}
                {action.details && action.action_type === 'redemption_deleted' && (
                  <p className="text-white/50 text-xs mt-0.5">
                    {(action.details as { benefit_type?: string }).benefit_type}
                    {(action.details as { reason?: string }).reason && (
                      <span className="block text-red-400/70 italic">"{(action.details as { reason?: string }).reason}"</span>
                    )}
                  </p>
                )}
                {action.details && action.action_type === 'purchase_deleted' && (
                  <p className="text-white/50 text-xs mt-0.5">
                    {(action.details as { product_name?: string }).product_name}
                    {(action.details as { reason?: string }).reason && (
                      <span className="block text-red-400/70 italic">"{(action.details as { reason?: string }).reason}"</span>
                    )}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-white/40 text-xs">
                    {format(new Date(action.created_at), "d MMM yyyy, HH:mm", { locale: es })}
                  </p>
                  {adminNames[action.admin_user_id] && (
                    <span className="text-xs text-amber-400/70">
                      por {adminNames[action.admin_user_id]}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default AdminActionsHistory;
